import axios from 'axios';
import config from '@/config';
import { toast } from 'react-toastify';
import authStore, { authActions } from '@/stores/authStore';
import { settingsActions } from '@/stores/settingsStore';
import * as Sentry from '@sentry/react';
import { parseErrorMessage } from '@/utils/errorUtils';

const { serverUrl } = config;
let errorId;

axios.defaults.withXSRFToken = true;
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';

axios.interceptors.request.use(reqConfig => {
  const { csrfToken } = authStore.getState();

  if (csrfToken) {
    if (['post', 'put', 'patch', 'delete'].includes(reqConfig.method.toLowerCase())) {
      reqConfig.headers['X-CSRFToken'] = csrfToken;
    }
  }

  if (!reqConfig.url.startsWith('http')) {
    reqConfig.url = `${serverUrl}${reqConfig.url}`;
  }

  return reqConfig;
});

axios.interceptors.response.use(
  response => {
    const serverAppVersion = response?.headers?.['x-version'];

    if (serverAppVersion) {
      const localAppVersion = settingsActions.getAppVersion();
      settingsActions.setAppVersion(serverAppVersion);

      if (localAppVersion && serverAppVersion !== localAppVersion) {
        window.location.reload();
      }
    }

    return response;
  },
  async error => {
    const requestUrl = error?.config?.url || '';
    const isSensitiveAuthRequest = /\/v1\/(login|user\/password|user\/mfa)\//.test(requestUrl);

    if (error?.response?.status === 403 || error?.response?.status > 500) {
      if (isSensitiveAuthRequest) return Promise.reject(error);
      return Sentry.captureException(error);
    }

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const loginErrorCode = error?.response?.data?.code;
    const isMfaLoginResponse =
      requestUrl.endsWith('/v1/login/') && ['mfa_required', 'invalid_totp', 'totp_throttled'].includes(loginErrorCode);

    if (isMfaLoginResponse) {
      return Promise.reject(error);
    } else if (error?.response?.status === 401) {
      authActions.logout();
    } else if (error?.config?.suppressErrorToast) {
      return Promise.reject(error);
    } else {
      // TODO: remove this after tea endpoint returns 200
      if (error?.request?.responseURL?.match(/tea|lca|coac|password|sweep|params|excel_models|custom_models/)) {
        return Promise.reject(error);
      }

      const messages = parseErrorMessage(error);
      const displayMessage = messages.join('\n');

      toast.clearWaitingQueue();

      if (!toast.isActive(errorId)) {
        errorId = toast.error(displayMessage);
      }
    }

    return Promise.reject(error);
  },
);
